var __extends = (this && this.__extends) || (function () {
    var extendStatics = function (d, b) {
        extendStatics = Object.setPrototypeOf ||
            ({ __proto__: [] } instanceof Array && function (d, b) { d.__proto__ = b; }) ||
            function (d, b) { for (var p in b) if (b.hasOwnProperty(p)) d[p] = b[p]; };
        return extendStatics(d, b);
    };
    return function (d, b) {
        extendStatics(d, b);
        function __() { this.constructor = d; }
        d.prototype = b === null ? Object.create(b) : (__.prototype = b.prototype, new __());
    };
})();
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import React, { useState } from 'react';
import { withSentryErrorBoundary, withStyles, } from '@wix/native-components-infra';
import memoize from 'lodash/memoize';
import { TPAComponentsProvider } from 'wix-ui-tpa/TPAComponentsConfig';
import { TpaSettingsProviderForWidget } from '@wix/tpa-settings/react';
import { ExperimentsProvider } from '@wix/wix-experiments-react';
import { I18nextProvider } from './react/i18next/I18nextProvider';
import i18n from './i18next';
import { ControllerProvider } from './react/Controller/ControllerProvider';
import { buildSentryOptions, getArtifact, biLoggerFromProps } from './utils';
import { WithProviders } from './react/utils';
import { BILoggerProvider } from './react/BILogger/BILoggerProvider';
import { PublicDataProviderViewer } from './react/PublicData/PublicDataProviderViewer';
import { withWidgetStylesOverrides } from './withWidgetStylesOverrides';
import { FedopsLoggerProvider } from './react/FedopsLoggerProvider';
import { WidgetEnvironment } from './environment/Widget';
import { WidgetEnvironmentConsumer, WidgetEnvironmentProvider, } from './react/EnvironmentProvider';
import { SentryProvider } from './react/Sentry';
var AppLoadedHandler = /** @class */ (function (_super) {
    __extends(AppLoadedHandler, _super);
    function AppLoadedHandler() {
        return _super !== null && _super.apply(this, arguments) || this;
    }
    AppLoadedHandler.prototype.componentDidMount = function () {
        if (this.props.onAppLoaded) {
            this.props.host.registerToComponentDidLayout(this.props.onAppLoaded);
        }
    };
    AppLoadedHandler.prototype.render = function () {
        return this.props.children;
    };
    return AppLoadedHandler;
}(React.Component));
// This widget is going to be called inside entry-point wrappers
// Each widget should contain component to wrap name, so here we return a getter instead of component.
var getWidgetWrapper = function (_a, UserComponent, _b) {
    var createHttpClientBridge = _a.createHttpClientBridge, HttpProvider = _a.HttpProvider;
    var name = _b.name, sentryConfig = _b.sentryConfig, isEditor = _b.isEditor, stylesParams = _b.stylesParams;
    var Widget = function (props) {
        var _language = props._language, _translations = props._translations, _multilingual = props._multilingual, _fedopsLogger = props._fedopsLogger, _sentry = props._sentry, _httpClient = props._httpClient, _enabledHOCs = props._enabledHOCs, _experiments = props._experiments, _mobile = props._mobile, _isRTL = props._isRTL, _isSSR = props._isSSR, _biMethods = props._biMethods, _biUtil = props._biUtil, _publicData = props._publicData, _appDefinitionId = props._appDefinitionId, _widgetId = props._widgetId, onAppLoaded = props.onAppLoaded, host = props.host, widgetProps = __rest(props, ["_language", "_translations", "_multilingual", "_fedopsLogger", "_sentry", "_httpClient", "_enabledHOCs", "_experiments", "_mobile", "_isRTL", "_isSSR", "_biMethods", "_biUtil", "_publicData", "_appDefinitionId", "_widgetId", "onAppLoaded", "host"]);
        var i18NextInstance;
        if (!_enabledHOCs) {
            if (_sentry) {
                void _sentry.captureException("Controller wasn't executed correctly for " + name + " component.");
            }
            throw new Error("Controller wasn't executed correctly for " + name + " component.\nSeems like the Widget is being rendered before the controller. Usually, that means that the Viewer Script wasn't loaded by the viewer platform or contains unhanded errors.\nPlease, contact the #viewer-platform-dev or #yoshi channel for more details.");
        }
        var biLogger = biLoggerFromProps({
            biMethods: _biMethods,
            biUtil: _biUtil,
        });
        var environment = new WidgetEnvironment({
            host: host,
            controllerOptions: {
                appDefinitionId: _appDefinitionId,
                widgetId: _widgetId,
                isSSR: !!_isSSR,
                isRTL: !!_isRTL,
                language: _language,
                multilingual: _multilingual !== null && _multilingual !== void 0 ? _multilingual : null,
            },
        });
        var httpClientBridge = useState(function () {
            return _httpClient != null ? createHttpClientBridge === null || createHttpClientBridge === void 0 ? void 0 : createHttpClientBridge(_httpClient) : null;
        })[0];
        var availableProviders = [
            function (children) { return (React.createElement(TPAComponentsProvider, { value: { mobile: _mobile, rtl: _isRTL } }, children)); },
            function (children) { return (React.createElement(ControllerProvider, { data: props }, children)); },
            function (children) { return (React.createElement(PublicDataProviderViewer, { data: _publicData.COMPONENT }, children)); },
        ];
        if (_enabledHOCs.experiments) {
            availableProviders.push(function (children) { return (React.createElement(ExperimentsProvider, { options: { experiments: _experiments } }, children)); });
        }
        if (_enabledHOCs.bi) {
            availableProviders.push(function (children) { return (React.createElement(BILoggerProvider, { logger: biLogger }, children)); });
        }
        availableProviders.push(function (children) { return (React.createElement(FedopsLoggerProvider, { value: _fedopsLogger }, children)); });
        availableProviders.push(function (children) { return (React.createElement(SentryProvider, { value: _sentry }, children)); });
        if (HttpProvider != null && httpClientBridge != null) {
            availableProviders.push(function (children) { return (React.createElement(HttpProvider, { client: httpClientBridge }, children)); });
        }
        if (_enabledHOCs.translations) {
            i18NextInstance = i18n({
                language: _language,
                translations: _translations,
            });
            availableProviders.push(function (children) { return (React.createElement(I18nextProvider, { i18n: i18NextInstance }, children)); });
        }
        availableProviders.push(function (children) { return (React.createElement(WidgetEnvironmentConsumer, null, function (environment) {
            var _a, _b, _c;
            return (React.createElement(TpaSettingsProviderForWidget, { isMobile: environment.isMobile, isRTL: environment.isRTL, publicData: _publicData, hostStyle: {
                    styleParams: (_a = host === null || host === void 0 ? void 0 : host.style) === null || _a === void 0 ? void 0 : _a.styleParams,
                    siteColors: (_b = host === null || host === void 0 ? void 0 : host.style) === null || _b === void 0 ? void 0 : _b.siteColors,
                    siteTextPresets: (_c = host === null || host === void 0 ? void 0 : host.style) === null || _c === void 0 ? void 0 : _c.siteTextPresets,
                }, language: (environment.multilingual.isEnabled &&
                    environment.multilingual.currentLanguage) ||
                    undefined, t: i18NextInstance
                    ? i18NextInstance.t.bind(i18NextInstance)
                    : undefined }, children));
        })); });
        // We want AppLoaderHandler to wrap all other providers;
        availableProviders.push(function (children) { return (React.createElement(AppLoadedHandler, { onAppLoaded: onAppLoaded, host: props.host }, children)); });
        availableProviders.push(function (children) { return (React.createElement(WidgetEnvironmentProvider, { value: environment }, children)); });
        return (React.createElement(WithProviders, { providers: availableProviders },
            React.createElement(UserComponent, __assign({ host: host }, widgetProps))));
    };
    var cssPath = isEditor
        ? name + "EditorMode.css"
        : name + "ViewerWidget.css";
    var stylablePath = isEditor
        ? name + "EditorMode.stylable.bundle.css"
        : name + "ViewerWidget.stylable.bundle.css";
    var WidgetComponent = withStyles(Widget, {
        cssPath: [cssPath, stylablePath],
        enableMemoization: typeof WeakMap === 'undefined' || memoize.Cache !== WeakMap,
    });
    if (sentryConfig) {
        WidgetComponent = withSentryErrorBoundary(WidgetComponent, buildSentryOptions(sentryConfig.DSN, 'Viewer', getArtifact()));
    }
    if (stylesParams) {
        WidgetComponent = withWidgetStylesOverrides(WidgetComponent, {
            stylesParams: stylesParams,
        });
    }
    return WidgetComponent;
};
export default getWidgetWrapper;
//# sourceMappingURL=WidgetWrapper.js.map